import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getData, postData } from "../../../FetchApiServices";
import { usePermissions } from "./T2TUIProvider";
import useBellSse from "../Notifications/useBellSse";

const T2TTopNav = ({ title = "", actions = null }) => {
  const user = useSelector((state) => state?.user?.users[0]);
  const { permissions: perms, loading: permsLoading } = usePermissions();
  // BUG-01: hasPermission() returns true during bootstrap (perms still loading
  // or no perms granted) which fired /active-delegation for users without the
  // flag → 403 console error on every page load. Require the flag to be
  // EXPLICITLY granted before treating the user as a delegator.
  const canDelegate = !permsLoading && perms?.["operations.delegate"] === true;

  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "T2";

  const openSidebar = () => {
    document.getElementById("t2t-sidebar")?.classList.add("open");
    document.getElementById("t2t-backdrop")?.classList.add("open");
  };

  // ── In-app notifications (Phase 14 + T2T-05-C5 live bell) ──
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen]     = useState(false);
  const [notifs, setNotifs]           = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [shaking, setShaking] = useState(false);
  const bellRef = useRef(null);
  const shakeTimerRef = useRef(null);

  // ── Delegation / OOO (Phase 15) ──
  const [delegModal, setDelegModal]       = useState(false);
  const [delegations, setDelegations]     = useState([]);
  const [delegLoading, setDelegLoading]   = useState(false);
  const [delegSaving, setDelegSaving]     = useState(false);
  const [delegError, setDelegError]       = useState("");
  const [activeDelegation, setActiveDelegation] = useState(null); // current OOO
  const [delegForm, setDelegForm] = useState({ delegate_id: "", start_date: "", end_date: "", scope: "all", workflow_ids: [] });
  const [t2tUsers, setT2tUsers]   = useState([]);

  // Fetch unread count on mount. With SSE wired below, the manual 30s poll is
  // gone — live pushes keep the count current. We still fetch once on mount
  // so the badge is correct before SSE connects.
  useEffect(() => {
    (async () => {
      const resp = await getData("t2t/notifications/unread-count");
      if (!resp.error) {
        setUnreadCount(resp.data?.data?.count ?? resp.data?.data?.unread_count ?? 0);
      }
    })();
  }, []);

  // T2T-05-C5: live bell stream — increments badge + shakes the icon.
  const onNotificationNew = useCallback((row) => {
    setUnreadCount((n) => n + 1);
    // If the dropdown is open, prepend the new row
    setNotifs((prev) => prev[0]?.id === row?.id ? prev : [row, ...prev].slice(0, 50));
    // Shake animation — 1s loop
    setShaking(true);
    if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
    shakeTimerRef.current = setTimeout(() => setShaking(false), 1000);
  }, []);
  useBellSse({ onNotificationNew });

  // Fetch full list when dropdown opens
  const openNotifPanel = async () => {
    setNotifOpen(true);
    setNotifLoading(true);
    const resp = await getData("t2t/notifications?limit=20");
    if (!resp.error) {
      const raw = resp.data?.data;
      setNotifs(Array.isArray(raw) ? raw : (Array.isArray(raw?.rows) ? raw.rows : []));
    }
    setNotifLoading(false);
  };

  const markAllRead = async () => {
    await postData("t2t/notifications/mark-all-read", {});
    setUnreadCount(0);
    setNotifs((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  // Fetch active delegation on mount — only for users with delegation permission
  useEffect(() => {
    if (!user?.user_id || !canDelegate) return;
    const fetchActive = async () => {
      const resp = await getData(`t2t/users/${user.user_id}/active-delegation`);
      if (!resp.error) {
        const d = resp.data?.data || null;
        setActiveDelegation(d && d.id ? d : null);
      }
    };
    fetchActive();
  }, [user?.user_id, canDelegate]);

  const openDelegModal = async () => {
    setDelegModal(true);
    setDelegError("");
    setDelegForm({ delegate_id: "", start_date: "", end_date: "", scope: "all", workflow_ids: [] });
    setDelegLoading(true);
    const [delegResp, usersResp] = await Promise.all([
      getData("t2t/delegations"),
      getData("users?is_t2t=true"),
    ]);
    if (!delegResp.error) {
      const raw = delegResp.data?.data;
      setDelegations(Array.isArray(raw) ? raw : []);
    }
    if (!usersResp.error) {
      const raw = usersResp.data?.data;
      setT2tUsers(Array.isArray(raw) ? raw.filter((u) => u.user_id !== user?.user_id) : []);
    }
    setDelegLoading(false);
  };

  const createDelegation = async () => {
    if (!delegForm.delegate_id) { setDelegError("Select a delegate."); return; }
    if (!delegForm.start_date || !delegForm.end_date) { setDelegError("Start and end dates are required."); return; }
    if (delegForm.start_date > delegForm.end_date) { setDelegError("End date must be after start date."); return; }
    setDelegError("");
    setDelegSaving(true);
    const resp = await postData("t2t/delegations", {
      delegate_id:  delegForm.delegate_id,
      start_date:   delegForm.start_date,
      end_date:     delegForm.end_date,
      scope:        delegForm.scope,
      workflow_ids:  delegForm.scope === "specific" ? delegForm.workflow_ids : [],
    });
    if (resp.error) {
      setDelegError(resp.error.message || "Failed to create delegation.");
    } else {
      setDelegForm({ delegate_id: "", start_date: "", end_date: "", scope: "all", workflow_ids: [] });
      // Refresh list
      const r2 = await getData("t2t/delegations");
      if (!r2.error) setDelegations(Array.isArray(r2.data?.data) ? r2.data.data : []);
    }
    setDelegSaving(false);
  };

  const cancelDelegation = async (delegId) => {
    const resp = await postData(`t2t/delegations/${delegId}/cancel`, {});
    if (resp.error) return;
    setDelegations((prev) => prev.map((d) => d.id === delegId ? { ...d, status: "cancelled" } : d));
    if (activeDelegation?.id === delegId) setActiveDelegation(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [notifOpen]);

  return (
    <header className="t2t-topnav">
      <button className="t2t-hamburger" onMouseEnter={openSidebar} title="Open menu">
        <i className="bi bi-list"></i>
      </button>
      <div className="t2t-topnav-title">{title}</div>
      <div className="t2t-topnav-actions">
        {actions}

        {/* Notification Bell */}
        <div ref={bellRef} style={{ position: "relative" }}>
          <button
            onClick={() => notifOpen ? setNotifOpen(false) : openNotifPanel()}
            className={shaking ? "t2t-bell-shake" : ""}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "var(--t2t-text-secondary)", position: "relative", padding: "4px 6px" }}
            title="Notifications"
          >
            <i className="bi bi-bell-fill"></i>
            {unreadCount > 0 && (
              <span style={{
                position: "absolute", top: 0, right: 0, background: "#DC2626", color: "#fff",
                fontSize: 9, fontWeight: 700, borderRadius: 10, padding: "1px 5px", minWidth: 16,
                textAlign: "center", lineHeight: "14px",
              }}>
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>

          {/* Dropdown */}
          {notifOpen && (
            <div style={{
              position: "absolute", top: "100%", right: 0, width: 340, maxHeight: 400,
              background: "#fff", border: "1px solid var(--t2t-border)", borderRadius: 10,
              boxShadow: "0 8px 30px rgba(0,0,0,.12)", zIndex: 1000, overflow: "hidden",
              display: "flex", flexDirection: "column",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderBottom: "1px solid var(--t2t-border)" }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>Notifications</span>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    style={{ background: "none", border: "none", fontSize: 11, color: "var(--t2t-primary)", cursor: "pointer", fontWeight: 600 }}
                  >
                    Mark all read
                  </button>
                )}
              </div>
              <div style={{ flex: 1, overflowY: "auto" }}>
                {notifLoading ? (
                  <div style={{ padding: 24, textAlign: "center", fontSize: 12, color: "var(--t2t-text-muted)" }}>Loading...</div>
                ) : notifs.length === 0 ? (
                  <div style={{ padding: 32, textAlign: "center", color: "var(--t2t-text-muted)" }}>
                    <div style={{ fontSize: 24, marginBottom: 6 }}>🔔</div>
                    <div style={{ fontSize: 12 }}>No notifications yet</div>
                  </div>
                ) : (
                  notifs.map((n) => {
                    const handleClick = async () => {
                      // Mark this single row read on click + navigate if action_url present
                      if (!n.is_read) {
                        try { await postData(`t2t/notifications/${n.id}/read`, {}); } catch (_) { /* swallow */ }
                        setNotifs((prev) => prev.map(x => x.id === n.id ? { ...x, is_read: 1 } : x));
                        setUnreadCount((c) => Math.max(0, c - 1));
                      }
                      if (n.action_url) {
                        setNotifOpen(false);
                        navigate(n.action_url);
                      }
                    };
                    return (
                      <div
                        key={n.id}
                        onClick={handleClick}
                        style={{
                          padding: "10px 14px", borderBottom: "1px solid #F1F5F9",
                          cursor: n.action_url ? "pointer" : "default",
                          background: n.is_read ? "transparent" : "#EFF6FF",
                        }}
                      >
                        <div style={{ fontSize: 12, fontWeight: n.is_read ? 400 : 700, color: "#1E293B", marginBottom: 2 }}>{n.title}</div>
                        <div style={{ fontSize: 11, color: "#64748B", lineHeight: 1.4 }}>{n.body}</div>
                        <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 4 }}>
                          {n.created_at ? new Date(n.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div style={{ borderTop: "1px solid var(--t2t-border)", padding: "8px 14px", display: "flex", justifyContent: "space-between" }}>
                <button
                  onClick={() => { setNotifOpen(false); navigate("/t2t/notifications"); }}
                  style={{ background: "none", border: "none", color: "var(--t2t-primary)", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                >
                  View all
                </button>
                <button
                  onClick={() => { setNotifOpen(false); navigate("/t2t/notifications/preferences"); }}
                  style={{ background: "none", border: "none", color: "var(--t2t-text-muted)", fontSize: 11, cursor: "pointer" }}
                >
                  Preferences
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Avatar + OOO indicator — click-to-delegate only if user has operations.delegate */}
        <div style={{ position: "relative" }}>
          <div
            className="t2t-avatar"
            style={{ width: 32, height: 32, fontSize: 12, cursor: canDelegate ? "pointer" : "default" }}
            title={canDelegate
              ? (activeDelegation ? `OOO — Delegating to ${activeDelegation.delegate_name || "delegate"}` : (user?.name || "T2T User"))
              : (user?.name || "T2T User")}
            onClick={canDelegate ? openDelegModal : undefined}
          >
            {initials}
          </div>
          {canDelegate && activeDelegation && (
            <span style={{
              position: "absolute", bottom: -2, right: -2, background: "#D97706", color: "#fff",
              fontSize: 8, fontWeight: 700, borderRadius: 4, padding: "1px 3px", lineHeight: 1,
            }}>OOO</span>
          )}
        </div>
      </div>

      {/* ── Delegation Modal (Phase 15) — gated by operations.delegate ── */}
      {canDelegate && delegModal && (
        <div className="t2t-overlay" style={{ zIndex: 9998 }} onClick={() => setDelegModal(false)}>
          <div className="t2t-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="t2t-modal-head">
              <div>
                <div className="t2t-modal-title">Out of Office / Delegation</div>
                <div className="t2t-modal-sub">Delegate your tasks while you are away</div>
              </div>
              <button className="t2t-close-btn" onClick={() => setDelegModal(false)}>×</button>
            </div>
            <div className="t2t-modal-body" style={{ maxHeight: 420, overflowY: "auto" }}>
              {delegLoading ? (
                <div style={{ textAlign: "center", padding: 24, color: "var(--t2t-text-muted)", fontSize: 12 }}>Loading...</div>
              ) : (
                <>
                  {/* Active delegation banner */}
                  {activeDelegation && (
                    <div style={{ padding: "10px 14px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, marginBottom: 14, fontSize: 12 }}>
                      <div style={{ fontWeight: 700, color: "#92400E", marginBottom: 4 }}>Currently Out of Office</div>
                      <div style={{ color: "#78350F" }}>
                        Delegating to <strong>{activeDelegation.delegate_name}</strong> from{" "}
                        {activeDelegation.start_date} to {activeDelegation.end_date}
                        {activeDelegation.status === "pending_approval" && (
                          <span style={{ marginLeft: 8, color: "#D97706", fontWeight: 600 }}>⏳ Pending approval</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Create new delegation */}
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>New Delegation</div>
                  {delegError && (
                    <div style={{ padding: "8px 12px", background: "#FEF2F2", color: "var(--t2t-danger)", borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{delegError}</div>
                  )}
                  <div className="t2t-fg">
                    <label>Delegate to *</label>
                    <select className="t2t-fc" value={delegForm.delegate_id}
                      onChange={(e) => setDelegForm({ ...delegForm, delegate_id: e.target.value })}>
                      <option value="">Select user...</option>
                      {t2tUsers.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} ({u.email})</option>)}
                    </select>
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <div className="t2t-fg" style={{ flex: 1 }}>
                      <label>Start Date *</label>
                      <input className="t2t-fc" type="date" value={delegForm.start_date}
                        onChange={(e) => setDelegForm({ ...delegForm, start_date: e.target.value })} />
                    </div>
                    <div className="t2t-fg" style={{ flex: 1 }}>
                      <label>End Date *</label>
                      <input className="t2t-fc" type="date" value={delegForm.end_date}
                        onChange={(e) => setDelegForm({ ...delegForm, end_date: e.target.value })} />
                    </div>
                  </div>
                  <div className="t2t-fg">
                    <label>Scope</label>
                    <select className="t2t-fc" value={delegForm.scope}
                      onChange={(e) => setDelegForm({ ...delegForm, scope: e.target.value })}>
                      <option value="all">All workflows</option>
                      <option value="specific">Specific workflows only</option>
                    </select>
                  </div>
                  <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={createDelegation} disabled={delegSaving} style={{ marginBottom: 16 }}>
                    {delegSaving ? "Submitting..." : "Request Delegation"}
                  </button>

                  {/* Delegation history */}
                  {delegations.length > 0 && (
                    <>
                      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, borderTop: "1px solid var(--t2t-border)", paddingTop: 12 }}>History</div>
                      {delegations.map((d) => {
                        const statusColor = { active: "#16A34A", pending_approval: "#D97706", expired: "#94A3B8", cancelled: "#DC2626" }[d.status] || "#94A3B8";
                        return (
                          <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 6, border: "1px solid #E2E8F0", marginBottom: 6, fontSize: 12 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600 }}>→ {d.delegate_name || "User"}</div>
                              <div style={{ color: "#64748B", fontSize: 11 }}>{d.start_date} to {d.end_date}</div>
                            </div>
                            <span style={{ fontSize: 10, fontWeight: 700, color: statusColor, background: statusColor + "18", padding: "2px 8px", borderRadius: 10 }}>
                              {d.status?.replace("_", " ")}
                            </span>
                            {(d.status === "active" || d.status === "pending_approval") && (
                              <button
                                className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                                style={{ fontSize: 10, color: "var(--t2t-danger)", padding: "2px 6px" }}
                                onClick={() => cancelDelegation(d.id)}
                              >Cancel</button>
                            )}
                          </div>
                        );
                      })}
                    </>
                  )}
                </>
              )}
            </div>
            <div className="t2t-modal-foot">
              <button className="t2t-btn t2t-btn-ghost" onClick={() => setDelegModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

T2TTopNav.propTypes = {
  title:   PropTypes.string,
  actions: PropTypes.node,
};

export default T2TTopNav;
